import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import {
  FormValues,
  useEventRecordingTranscribeItemInputForm,
} from './useEventRecordingTranscribeItemInputForm'
import Cancel from '~icons/material-symbols/cancel'
import Save from '~icons/material-symbols/save'
import { useShowApiErrorsWithForm } from 'hooks/useShowApiErrors'
import { CrewCheckBoxField } from 'components/forms/crewCheckBoxField'
import { CrewTextAreaField } from 'components/forms/crewTextAreaField'
import { useTranslation } from '@crew/modules/i18n'
import { CrewSelectBoxField } from 'components/forms/crewSelectBoxField'
import { ValueChangedEvent } from 'devextreme/ui/select_box'
import { useToast } from 'hooks/useToast'
import { CrewUserAvatar } from 'components/elements/crewUserAvatar/crewUserAvatar'
import { EventRecordingTranscribe } from '@crew/apis/eventRecordingTranscribe/models/getEventRecordingTranscribes/response'
import AccountCircle from '~icons/material-symbols/account-circle'
import {
  ComponentCallbackHandler,
  convertSecondsToHHMMSS,
  convertSecondsToMMSS,
} from '@crew/utils'
import { UserRef } from '@crew/models/refs'
import {
  IsApiErrorRequiredSpeakerNameOrId,
  isApiErrorResult,
} from '@crew/apis/errors'
import {
  AudioState,
  EventRecordingTranscribeAudioPlayButton,
} from '../eventRecordingTranscribeAudioPlayButton/eventRecordingTranscribeAudioPlayButton'

export type EventRecordingTranscribeItemInputFormProps = {
  isEditMode: boolean // 行修正モードかどうか
  maxSpeechTime: number | undefined // この文字起こしが属するイベント録画内の発言時刻の最大時間
  eventRecordingTranscribe: EventRecordingTranscribe // 文字起こしデータ
  onClose: () => void // 入力フォームを閉じる関数
  // 次の文字起こしの開始時刻
  nextSpeechTimestamp: number | null
  // 音声再生制御
  audioState: AudioState
  audioCurrentRequester: string | null
  audioPlay?: (requester: string, start: number, end: number | null) => void
  audioStop?: () => void
}

// 定数を定義
const TEMP_USER_ID_PREFIX = 'temp-'

export const EventRecordingTranscribeItemInputForm = memo(
  (props: EventRecordingTranscribeItemInputFormProps) => {
    const {
      control,
      reset,
      getValues,
      setValue,
      formState,
      handleSubmit,
      setError,
      clearErrors,

      validateRules,

      speakerDataSource,

      insertEventRecordingTranscribe,
      updateEventRecordingTranscribe,
      isLoadingInsertTranscribe,
      isLoadingUpdateTranscribe,
    } = useEventRecordingTranscribeItemInputForm(props)

    const { t } = useTranslation()
    const toast = useToast()
    const [showApiErrors] = useShowApiErrorsWithForm(setError)

    const canSend = useMemo(
      // fromState.isValidはerrorsが空でもfalseになることがあるためerrorsで判定する
      () =>
        Object.keys(formState.errors).length === 0 && !formState.isSubmitting,
      // formStateはproxyなのでformState自体をlistenする必要がある
      // https://react-hook-form.com/api/useform/formstate
      [formState]
    )
    const [selectedUser, setSelectedUser] = useState<UserRef | null>(null)

    // 一時的なユーザー情報を作成する
    const createTempUser = (displayName: string) => {
      const tempUser: UserRef = {
        id: `temp-${Date.now()}`,
        displayName,
        version: 1,
      }
      return tempUser
    }

    // フォーム初期処理関数
    const initializeForm = useCallback(() => {
      const formInitialValues: FormValues = {
        bulkUpdate: false,
        speakerId: null,
        speakerName: null,
        speachContent: '',
      }

      setSelectedUser(null)

      if (props.isEditMode && props.eventRecordingTranscribe) {
        // Formの初期値を設定
        formInitialValues.speachContent =
          props.eventRecordingTranscribe.speechContent

        if (!props.eventRecordingTranscribe.speakerId) {
          // 発言者ID未設定＝発言者名のみ設定
          // 発言者がフリー入力の場合、一時的なユーザー情報を作成する
          const tempUser = createTempUser(
            props.eventRecordingTranscribe.speakerName ?? ''
          )
          formInitialValues.speakerId = tempUser.id
          formInitialValues.speakerName = null
          setSelectedUser(tempUser)
        } else {
          // 発言者IDが設定済み
          formInitialValues.speakerId = props.eventRecordingTranscribe.speakerId
          formInitialValues.speakerName =
            props.eventRecordingTranscribe.speakerName
          setSelectedUser(props.eventRecordingTranscribe.speaker)
        }
      }
      // フォームをリセット
      reset(formInitialValues)
    }, [props, reset])

    useEffect(() => {
      // フォーム初期化を実行
      initializeForm()
    }, [initializeForm])

    // 保存ボタンクリック時
    const handleSaveButtonClick = useCallback(() => {
      const onSubmit = async (data: FormValues) => {
        try {
          if (props.isEditMode) {
            // 行修正
            await updateEventRecordingTranscribe(
              props.eventRecordingTranscribe.id,
              props.eventRecordingTranscribe.version,
              data
            )
            props.onClose()
            toast.success(
              t(
                'message.eventRecordingTranscribe.eventRecordingTranscribeUpdated'
              )
            )
          } else {
            // 行追加
            await insertEventRecordingTranscribe(
              props.eventRecordingTranscribe.eventRecordingId,
              props.eventRecordingTranscribe.position ?? 0,
              data
            )
            props.onClose()
            toast.success(
              t(
                'message.eventRecordingTranscribe.eventRecordingTranscribeRegistered'
              )
            )
          }
          reset()
          clearErrors()
        } catch (err: unknown) {
          if (
            isApiErrorResult(err) &&
            IsApiErrorRequiredSpeakerNameOrId(err.data)
          ) {
            // 発言者IDと発言者名の必須チェックに引っかかった場合
            setError('speakerId', {
              type: 'manual',
              message: t('message.apiError.requiredSpeakerNameOrId'),
            })
          } else {
            // サーバーエラーの場合はトースト表示
            showApiErrors(err)
          }
        }
      }

      // 更新・登録が可能な状態かチェック
      if (
        !canSend ||
        (props.isEditMode && isLoadingUpdateTranscribe) ||
        (!props.isEditMode && isLoadingInsertTranscribe)
      ) {
        return
      }

      // 発言者をフリー入力した場合、SpeakerIdをnullにしてSpeakerNameをセットする
      const tempId = getValues('speakerId')
      if (tempId && tempId.startsWith(TEMP_USER_ID_PREFIX) && selectedUser) {
        setValue('speakerId', null)
        setValue('speakerName', selectedUser.displayName)
      }
      handleSubmit(onSubmit)()
    }, [
      canSend,
      clearErrors,
      getValues,
      handleSubmit,
      insertEventRecordingTranscribe,
      isLoadingInsertTranscribe,
      isLoadingUpdateTranscribe,
      props,
      reset,
      selectedUser,
      setError,
      setValue,
      showApiErrors,
      t,
      toast,
      updateEventRecordingTranscribe,
    ])

    // キャンセルボタンクリック時
    const handleCancelButtonClick = useCallback(() => {
      props.onClose()
    }, [props])

    // 発言者選択ボックスの値変更時
    const handleSpeakerIdChanged = useCallback(
      (event: ValueChangedEvent) => {
        const newSpeakerId = event.value as string
        if (!newSpeakerId) {
          // 選択中の発言者情報をリセットする
          setSelectedUser(null)
          return
        }

        // SpeakerIdがセットされた場合は、SpeakerNameをクリアする
        setValue('speakerName', null)

        // 選択中の発言者情報を保持する
        const selectedData = event.component.option('selectedItem') as UserRef
        setSelectedUser(selectedData)
      },
      [setValue]
    )

    // 発言者選択ボックスのカスタムアイテム作成時
    const handleSpeakerCustomItemCreating = useCallback<
      ComponentCallbackHandler<
        typeof CrewSelectBoxField,
        'onCustomItemCreating'
      >
    >((args) => {
      if (!args.text) {
        return
      }

      // 発言者にフリー入力の値が選択された場合は、一時的なユーザー情報を作成する
      const tempUser = createTempUser(args.text)
      // 選択された値としてセット
      args.customItem = tempUser
    }, [])

    // 発言時刻のrender
    const renderSpeechTime = useCallback(
      (second: number) => {
        const hour = 60 * 60

        // 発言時刻の最大時間が取得できない場合は何も描画しない
        if (!props.maxSpeechTime) {
          return null
        }

        // 小数点以下は表示しないため切り捨てる
        const roundedSecond = Math.floor(second)

        // 発言時間の最大値によって表示形式を決める
        //   →1時間以上の発言がある　場合：[h:mm:ss]形式で表示
        //   →1時間未満の発言しかない場合：[m:ss]形式で表示
        let speechTime
        if (props.maxSpeechTime > hour) {
          // 1時間以上の発言がある場合は、[h:mm:ss]形式で表示
          speechTime = convertSecondsToHHMMSS(roundedSecond)
        } else {
          // 1時間未満の発言しかない場合は、[m:ss]形式で表示
          speechTime = convertSecondsToMMSS(roundedSecond)
        }

        return speechTime
      },
      [props.maxSpeechTime]
    )

    // アバターを表示するためのユーザー情報を設定
    const avatarUser = useMemo(() => {
      if (selectedUser && !selectedUser.id.startsWith(TEMP_USER_ID_PREFIX)) {
        return selectedUser
      }
      return null
    }, [selectedUser])

    // 再生ボタンを表示するか
    const isShowPlayButton = useMemo(() => {
      // 新規登録(＝手入力)の場合、再生ボタンは表示しない
      if (!props.isEditMode) {
        return false
      }

      // 発言時刻が入力されている（＝自動で文字起こしされた発言内容）の場合、再生ボタンを表示する
      return props.eventRecordingTranscribe.speechTimestamp != null
    }, [props.isEditMode, props.eventRecordingTranscribe.speechTimestamp])

    return (
      <form className="flex flex-row gap-x-1 hover:bg-crew-gray-1-light hover:dark:bg-crew-gray-4-dark">
        {/* アバター */}
        <div>
          {avatarUser ? (
            <div
              className="m-[4px]" // AccountCircleのsvgに余白が発生する関係で表示位置がずれるため、マージンを入れて調整
            >
              {/* 発言者IDが登録されている場合：ユーザーのアバターを表示 */}
              <CrewUserAvatar
                userId={avatarUser.id}
                displayName={avatarUser.displayName}
                cacheValue={avatarUser.id + avatarUser.version}
              />
            </div>
          ) : (
            // 発言者が登録されていない or 発言者名のみ登録されている場合：デフォルトのアバターを表示
            <AccountCircle
              width={48}
              height={48}
              className={'crew-text-gray-5'}
            />
          )}
        </div>
        <div className="flex flex-col flex-1 gap-2">
          <div className="flex flex-row gap-2 items-center">
            {/* 発言者 */}
            <CrewSelectBoxField
              id="speakerId"
              name="speakerId"
              control={control}
              dataSource={speakerDataSource}
              labelMode="hidden"
              valueExpr="id"
              displayExpr="displayName"
              searchExpr="displayName"
              searchEnabled={true}
              minSearchLength={0}
              acceptCustomValue={true}
              onCustomItemCreating={handleSpeakerCustomItemCreating}
              rules={validateRules.speakerId}
              onValueChanged={handleSpeakerIdChanged}
              selectedItem={selectedUser}
            />
            {/* 行修正時のみ表示 */}
            {props.isEditMode && (
              <>
                {/* 同じ話者の発言を一括で更新する */}
                <CrewCheckBoxField
                  name="bulkUpdate"
                  control={control}
                  rules={validateRules.bulkUpdate}
                  label={t('label.bulkUpdateSameSpeaker')}
                  className="crew-text-default"
                />
                {/* 発言時刻 */}
                <div className="crew-text-gray-5">
                  {props.eventRecordingTranscribe.speechTimestamp &&
                    renderSpeechTime(
                      props.eventRecordingTranscribe.speechTimestamp
                    )}
                </div>
              </>
            )}
          </div>
          <div className="flex flex-row gap-x-2 items-center">
            <div className="flex-1">
              {/* 発言内容 */}
              <CrewTextAreaField
                id="speachContent"
                name="speachContent"
                control={control}
                height={50}
                labelMode="hidden"
                showLabel={false}
                rules={validateRules.speachContent}
              />
            </div>
            <div className="flex flex-row gap-x-2">
              {/* 再生ボタン */}
              {isShowPlayButton && (
                <EventRecordingTranscribeAudioPlayButton
                  eventRecordingTranscribeId={props.eventRecordingTranscribe.id}
                  speechTimestamp={
                    props.eventRecordingTranscribe.speechTimestamp ?? 0
                  }
                  nextSpeechTimestamp={props.nextSpeechTimestamp}
                  audioState={props.audioState}
                  audioCurrentRequester={props.audioCurrentRequester}
                  audioPlay={props.audioPlay}
                  audioStop={props.audioStop}
                />
              )}
              {/* 保存ボタン */}
              <div
                className="cursor-pointer hover:opacity-70"
                onClick={handleSaveButtonClick}
              >
                <Save width={24} height={24} />
              </div>

              {/* キャンセルボタン */}
              <div
                className="cursor-pointer hover:opacity-70"
                onClick={handleCancelButtonClick}
              >
                <Cancel width={24} height={24} />
              </div>
            </div>
          </div>
        </div>
      </form>
    )
  }
)
